import React from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, Seo } from "@shared/components";
import { buildBotUrl } from "@shared/helper_lib/bot-link";
import { Badge, Button, Card } from "@shared/ui/components";
import { TelegramIcon } from "@shared/ui/icons";
import {
  ArrowRight,
  Cloud,
  FileText,
  Globe,
  Link2,
  MessageCircle,
  Mic,
  PlayCircle,
  Sparkles,
  Users,
  Zap
} from "lucide-react";

const features = [
  {
    icon: MessageCircle,
    title: "Прямо в мессенджере",
    description: "Отправляйте голосовые, видео и аудио прямо в чат — без открытия браузера"
  },
  {
    icon: Link2,
    title: "Единый аккаунт",
    description: "Свяжите Telegram с веб-аккаунтом — минуты и история синхронизируются"
  },
  {
    icon: PlayCircle,
    title: "Ссылки на видео",
    description: "YouTube, VK Видео, Rutube, OK.ru — просто отправьте ссылку"
  },
  {
    icon: Cloud,
    title: "Облачные хранилища",
    description: "Google Drive и Яндекс.Диск — загружайте файлы по ссылке"
  },
  {
    icon: Sparkles,
    title: "AI краткое содержание",
    description: "Получите структурированное резюме с ключевыми моментами"
  },
  {
    icon: Users,
    title: "Распознавание спикеров",
    description: "Автоматическое разделение речи по говорящим"
  },
  {
    icon: FileText,
    title: "Экспорт в любой формат",
    description: "SRT для субтитров, DOCX и PDF — прямо в Telegram"
  },
  {
    icon: Globe,
    title: "99 языков",
    description: "Автоматическое определение языка или выбор вручную"
  }
];

const steps = [
  {
    number: "1",
    title: "Откройте бота",
    description: "Нажмите кнопку ниже или найдите @PolontoTranscribe_bot в Telegram"
  },
  {
    number: "2",
    title: "Отправьте файл или ссылку",
    description: "Голосовое, видео, аудио или ссылку на YouTube/VK/облако"
  },
  {
    number: "3",
    title: "Получите текст",
    description: "Транскрипция придёт в чат, можно сразу экспортировать"
  }
];

export const TelegramPage: React.FC = () => {
  return (
    <>
      <Seo
        title="Бот для расшифровки голосовых и аудио в Telegram"
        description="Перешлите боту голосовое, аудиофайл или видео — вернётся текст со спикерами и таймкодами. Файлы до 2 ГБ, сайт открывать не нужно. Пробный период — 150 минут за 99 ₽."
        path="/telegram"
      />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: "Telegram бот" }]} />

      <main>
        <section className="relative overflow-hidden border-b border-border bg-background py-16 md:py-24">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="mb-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl text-balance">
                <span className="font-rublev">Т</span>ранскрибация прямо в{" "}
                <span className="text-[#2AABEE]">Telegram</span>
              </h1>

              <p className="mb-8 text-lg text-muted-foreground text-pretty md:text-xl">
                Отправляйте голосовые сообщения, видео и ссылки — получайте текст без открытия браузера. Все возможности
                Полотно теперь в вашем любимом мессенджере.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-[#2AABEE] hover:bg-[#229ED9] text-white"
                  onClick={() => window.open(buildBotUrl(), "_blank")}
                >
                  <TelegramIcon className="mr-2 h-5 w-5" />
                  Открыть бота
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">Посмотреть тарифы</Link>
                </Button>
              </div>

              <p className="mt-6 text-sm font-light text-muted-foreground">
                Пробный период 150 минут за 99₽ • Единый баланс с веб-версией
              </p>
            </div>
          </div>

          <div className="absolute inset-x-0 top-0 -z-10 h-full bg-[radial-gradient(ellipse_at_top,_#2AABEE_0%,_transparent_50%)] opacity-10" />
        </section>

        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <div className="mb-12 text-center">
              <h2 className="mb-3 text-3xl font-bold text-foreground md:text-4xl">
                Всё, что умеет веб-версия — и даже больше
              </h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                Полный функционал транскрипции в удобном формате чата
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {features.map(feature => (
                <Card key={feature.title} className="p-6 hover:shadow-lg transition-shadow">
                  <feature.icon className="mb-4 h-10 w-10 text-[#2AABEE]" />
                  <h3 className="mb-2 text-lg font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 border-t border-border bg-muted/30">
          <div className="container">
            <div className="mb-12 text-center">
              <h2 className="mb-3 text-3xl font-bold text-foreground md:text-4xl">Как это работает</h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">Три простых шага до готового текста</p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {steps.map(step => (
                <div key={step.number} className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#2AABEE] text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-foreground">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 border-t border-border">
          <div className="container">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-[#2AABEE]/10 to-transparent border-[#2AABEE]/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center gap-2 mb-4 justify-center md:justify-start">
                    <Link2 className="h-6 w-6 text-[#2AABEE]" />
                    <h2 className="text-2xl font-bold text-foreground">Свяжите аккаунты</h2>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Уже используете веб-версию? Свяжите Telegram с вашим аккаунтом командой{" "}
                    <code className="px-2 py-1 bg-muted rounded text-sm">/link</code> — минуты, тариф и история
                    транскрипций синхронизируются автоматически.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-[#2AABEE]" />
                      Единый баланс минут
                    </li>
                    <li className="flex items-center gap-2">
                      <Mic className="h-4 w-4 text-[#2AABEE]" />
                      Общая история транскрипций
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-[#2AABEE]" />
                      Работайте там, где удобно
                    </li>
                  </ul>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    size="lg"
                    className="bg-[#2AABEE] hover:bg-[#229ED9] text-white"
                    onClick={() => window.open(buildBotUrl(), "_blank")}
                  >
                    <TelegramIcon className="mr-2 h-5 w-5" />
                    Связать аккаунт
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <section className="py-16 md:py-24 border-t border-border bg-muted/30">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl">Готовы попробовать?</h2>
              <p className="mb-8 text-lg text-muted-foreground">Пробный период 99 рублей за 150 минут.</p>
              <Button
                size="lg"
                className="bg-[#2AABEE] hover:bg-[#229ED9] text-white"
                onClick={() => window.open(buildBotUrl(), "_blank")}
              >
                <TelegramIcon className="mr-2 h-5 w-5" />
                Начать в Telegram
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};
